import React from 'react';
import { Platform, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { COLORS } from '../constants/theme';
import { useNextEvents } from '../hooks/useNextEvents';
import type { EventDetail } from '../types';
import { OwnershipScope } from '../types/favourites';
import { formatEventFooterPercent, formatEventMaturityAmount } from '../utils/eventDisplay';
import { formatEventDate } from '../utils/notificationDisplay';
import { SkeletonCard } from './SkeletonLoader';

interface HomeNextEventsProps {
  readonly includeWarrants?: boolean;
  readonly ownershipScope?: OwnershipScope;
  readonly onEventPress?: (isin: string) => void;
  readonly onGoToEvents?: () => void;
}

const eventKey = (event: EventDetail, index: number): string =>
  `${event.productId}-${event.eventType}-${String(event.eventDate).slice(0, 10)}-${index}`;

export const HomeNextEvents: React.FC<HomeNextEventsProps> = ({
  includeWarrants = false,
  ownershipScope = 'group',
  onEventPress,
  onGoToEvents,
}) => {
  const { events, isLoading, isError } = useNextEvents(includeWarrants, ownershipScope);

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Next events</Text>
        {onGoToEvents ? (
          <TouchableOpacity onPress={onGoToEvents} activeOpacity={0.8}>
            <Text style={styles.seeAll}>See all events</Text>
          </TouchableOpacity>
        ) : null}
      </View>

      {isLoading ? (
        <View style={styles.emptyState}>
          <SkeletonCard />
        </View>
      ) : isError ? (
        <View style={styles.emptyState}>
          <Text style={styles.emptyText}>Could not load upcoming events.</Text>
        </View>
      ) : events.length === 0 ? (
        <View style={styles.emptyState}>
          <Text style={styles.emptyText}>No events in the next 14 days.</Text>
        </View>
      ) : (
        <ScrollView
          style={[styles.list, Platform.OS === 'web' ? styles.listWeb : null]}
          contentContainerStyle={styles.listContent}
          nestedScrollEnabled
          showsVerticalScrollIndicator
          keyboardShouldPersistTaps="handled"
        >
          {events.map((event, index) => {
            const dateLabel = formatEventDate(String(event.eventDate));
            const isin = event.isin?.trim() || '';
            const isMaturity = event.eventDescription === 'Product Maturity';
            const amountLabel = isMaturity
              ? formatEventMaturityAmount(event.amount, event.currency)
              : formatEventFooterPercent(
                  event.firstCouponAmountPct,
                  event.amount,
                  event.notional,
                  event.couponPercent
                );

            return (
              <TouchableOpacity
                key={eventKey(event, index)}
                style={[styles.item, index === events.length - 1 && styles.itemLast]}
                activeOpacity={0.8}
                onPress={() => {
                  if (isin) {
                    onEventPress?.(isin);
                  }
                }}
                disabled={!onEventPress || !isin}
              >
                <Text style={styles.headline} numberOfLines={1}>
                  {dateLabel ? <Text style={styles.dateText}>{`Event date: ${dateLabel}`}</Text> : null}
                  {dateLabel && isin ? <Text style={styles.separator}>{' | '}</Text> : null}
                  {isin ? <Text style={styles.isinText}>{isin}</Text> : null}
                  {(dateLabel || isin) && event.eventDescription ? (
                    <Text style={styles.separator}>{' | '}</Text>
                  ) : null}
                  <Text style={styles.titleText}>{event.eventDescription}</Text>
                </Text>
                <Text style={styles.body} numberOfLines={1}>
                  {event.productName}
                </Text>
                <Text style={styles.amount}>{amountLabel}</Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  header: {
    backgroundColor: COLORS.primary,
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    fontSize: 16,
    color: COLORS.white,
    fontFamily: 'Montserrat_700Bold',
  },
  seeAll: {
    fontSize: 12,
    color: COLORS.chartSecondary,
    fontFamily: 'Montserrat_600SemiBold',
  },
  emptyState: {
    paddingHorizontal: 16,
    paddingVertical: 20,
  },
  emptyText: {
    fontSize: 13,
    color: COLORS.textSecondary,
    fontFamily: 'Montserrat_500Medium',
  },
  list: {
    maxHeight: 280,
  },
  listWeb: {
    overflow: 'scroll',
  },
  listContent: {
    flexGrow: 1,
  },
  item: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F4F4F4',
  },
  itemLast: {
    borderBottomWidth: 0,
  },
  headline: {
    fontSize: 13,
    marginBottom: 4,
  },
  dateText: {
    fontSize: 13,
    color: COLORS.chartAccent,
    fontFamily: 'Montserrat_600SemiBold',
  },
  separator: {
    fontSize: 13,
    color: '#9CA3AF',
    fontFamily: 'Montserrat_500Medium',
  },
  isinText: {
    fontSize: 13,
    color: COLORS.primary,
    fontFamily: 'Montserrat_600SemiBold',
  },
  titleText: {
    fontSize: 13,
    color: COLORS.text,
    fontFamily: 'Montserrat_600SemiBold',
  },
  body: {
    fontSize: 12,
    color: COLORS.textSecondary,
    fontFamily: 'Montserrat_500Medium',
    lineHeight: 17,
    marginBottom: 2,
  },
  amount: {
    fontSize: 12,
    color: COLORS.text,
    fontFamily: 'Montserrat_600SemiBold',
  },
});
